import { useMutation, useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import toast from 'react-hot-toast'
import { userOrganizationsKeys } from 'utils/query-keys'

export interface Team {
  id: string | number
  name: string
  users_count: number
  created_at: string
}

export interface AddUserToOrgResponse {
  data: {
    user_organization: any
    available_teams?: Team[]
  }
}

const fetchCreateUserOrganization = async (
  user_id: string,
  organization_id: string,
  is_admin: boolean,
  team_id?: string | number
): Promise<AddUserToOrgResponse> => {
  const body: any = {
    user_id,
    organization_id,
    is_admin,
    remove_last_admin: true,
  }

  if (team_id) {
    body.team_id = team_id
  }

  const response = await fetch(
    `${BASE_API_URL}/admin/api/v2/user_organizations`,
    {
      method: API_METHODS.POST,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(
      errorData?.error?.messages?.[0] ||
        errorData?.message ||
        'Failed to add user'
    )
  }

  return response.json() as Promise<AddUserToOrgResponse>
}

interface AddUserVariables {
  userId: string
  organizationId: string
  isAdmin: boolean
  teamId?: string | number
}

const useMutationAddUserToOrganization = ({ orgId }: { orgId: string }) => {
  const queryClient = useQueryClient()
  return useMutation<AddUserToOrgResponse, Error, AddUserVariables>(
    ({ userId, organizationId, isAdmin, teamId }: AddUserVariables) => {
      return fetchCreateUserOrganization(
        userId,
        organizationId,
        isAdmin,
        teamId
      )
    },
    {
      // When mutate is called:
      onMutate: () => {},
      onSuccess: (data: AddUserToOrgResponse) => {
        const teams = data?.data?.available_teams
        const hasMultipleTeams = teams && teams.length > 1

        if (hasMultipleTeams) {
          // When multiple teams are available, don't show a success toast.
          // The caller will handle team selection.
        }
      },
      onError: (error: Error) => {
        console.error('Add user to organization error:', error)

        // Check if the error is related to active Texting Betty integration
        const errorMessage = error?.message || 'Failed to add user.'
        if (errorMessage.includes('Texting Betty')) {
          toast.error(errorMessage, {
            duration: 5000,
            icon: '⚠️',
          })
        } else {
          // Generic error handling
          toast.error(errorMessage, {
            duration: 4000,
            icon: '❌',
          })
        }
      },
      // Always refetch after error or success:
      onSettled: () => {
        queryClient.invalidateQueries(userOrganizationsKeys.detail(orgId))
      },
    }
  )
}

export default useMutationAddUserToOrganization
